import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { Splide, SplideSlide } from '@splidejs/react-splide';
import { nuDSColor } from '@nubank/nuds-web/styles/themeUtils';
import styled from 'styled-components';
import Box from '@nubank/nuds-web/components/Box/Box';
import Image from '@nubank/nuds-web/components/Image/Image';
import Typography from '@nubank/nuds-web/components/Typography/Typography';
import Link from '@nubank/nuds-web/components/Link/Link';
import { createPortal } from 'react-dom';

import PartnerModal from './PartnerModal';

const StyledSplide = styled(Splide)`

  .splide__pagination {
    display: flex;
    justify-content: left;
    padding-top: 24px;
    padding-left: 24px;
  }

  .splide__pagination__page {
    border-radius: 12px;
    width: 8px;
    height: 8px;
    margin-right: 20px;
    background-color: ${nuDSColor('black.defaultT20')};
    
    &.is-active {
      width: 16px;
      background-color: ${nuDSColor('primary')};
    }
  }
`;

const StyledTypography = styled(Typography)`
  max-width: ${props => `${props.totalChars / 2}ch`};
  max-height: 72px;
  overflow: hidden;
  display: -webkit-box;
  -webkit-line-clamp: 3;
  -webkit-box-orient: vertical;
  text-overflow: ellipsis;
`;

const StyledLink = styled(Link)`
  display: flex;
  align-items: center;
  width: fit-content;
`;

function PartnersCarousel({ partnersList }) {
  const [isOpen, setIsOpen] = useState(false);
  const [selectedItem, setSelectedItem] = useState(null);

  const handleItemClick = itemId => {
    const selectedPartner = partnersList.find(item => item.id === itemId);
    setSelectedItem(selectedPartner);
    setIsOpen(true);
  };

  const closeModal = () => {
    setIsOpen(false);
  };

  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'visible';
    }
  }, [isOpen]);

  return (
    <>
      <StyledSplide
        aria-label="mobile-partners-slides"
        options={{
          arrows: false,
          perPage: 1,
          mediaQuery: 'min',
          breakpoints: {
            600: { perPage: 2 },
          },
          gap: '20px',
          fixedHeight: '347px',
          fixedWidth: '300px',
          padding: { right: '48px' },
        }}
      >

        {partnersList.map(partner => (
          <SplideSlide key={partner.id}>
            <Box
              borderRadius="12px"
              height={{ xs: '347px', md: 'auto' }}
              width="300px"
              marginLeft="24px"
              display="flex"
              flexDirection="column"
              alignItems="center"
              justifyContent="space-between"
              paddingBottom={{ xs: '30px', md: '0' }}
              paddingTop={{ xs: '6x', md: '0' }}
              paddingHorizontal={{ xs: '19px', md: '0' }}
              backgroundColor="white.default"
              onClick={() => handleItemClick(partner.id)}
            >
              <Image
                alt={partner.image.alt}
                src={partner.image.url}
                width={{ xs: '133px' }}
                height={{ xs: '133px' }}
                loading="lazy"
                objectFit="contain"
                webp
              />

              <Box flex="1" display="flex" flexDirection="column" justifyContent="center" minHeight="72px">
                <StyledTypography
                  variant="heading4"
                  strong
                  marginTop="27px"
                  marginBottom="35px"
                  textAlign="center"
                  totalChars={partner.title.length}
                >
                  {partner.title}
                </StyledTypography>
              </Box>

              {/* Link that works as a button ¯\_(ツ)_/¯ */}

              <StyledLink
                href="###"
                alt="Ver beneficio"
                variant="action"
                iconProps={{
                  name: 'arrow-right',
                }}
                onClick={e => {
                  e.preventDefault();
                  handleItemClick(partner.id);
                }}
              >
                Ver beneficio
              </StyledLink>
            </Box>
          </SplideSlide>
        ))}
      </StyledSplide>

      {/* MODAL OVERLAY FOR MORE DETAILS */}

      {isOpen && selectedItem && createPortal(
        <PartnerModal closeModal={closeModal} item={selectedItem} isOpen={isOpen} />, document.getElementById('modal-root'),
      )}
    </>

  );
}

PartnersCarousel.propTypes = {
  partnersList: PropTypes.arrayOf(
    PropTypes.shape({
      description: PropTypes.string,
      id: PropTypes.string,
      image: PropTypes.shape({
        alt: PropTypes.string,
        url: PropTypes.string,
      }),
      moreLink: PropTypes.string,
      title: PropTypes.string,
    }),
  ).isRequired,
};

export default PartnersCarousel;
